// Display-label mappings for enum values coming from the API.
// Formatters use i18next so labels are localised (e.g. "Empleado" in Spanish).
// Fallback Record maps are kept for when i18next isn't initialised yet.

import i18next from "i18next";

import type {
  AttendanceStatus,
  AuthMethod,
  BreakPolicy,
  BreakType,
  ClockStatus,
  EmploymentType,
  EventType,
  Gender,
  LeaveStatus,
  MaritalStatus,
  PayFrequency,
  PaySchedule,
  PayType,
  RequisitionStatus,
  Role,
  VisaStatus,
} from "../types";

// --- helpers ----------------------------------------------------------------

function tEnum(ns: string, key: string, fallback: string): string {
  if (!i18next.isInitialized) return fallback;
  return i18next.t(`${ns}.${key}`, { defaultValue: fallback });
}

// --- Roles ------------------------------------------------------------------

const ROLE_FALLBACK: Record<Role, string> = {
  employee: "Employee",
  manager: "Manager",
  hr_admin: "HR Admin",
  super_admin: "Super Admin",
};

export function formatRole(role: Role): string {
  return tEnum("roles", role, ROLE_FALLBACK[role] ?? role);
}

export const ROLE_OPTIONS: readonly { value: Role; label: string }[] = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr_admin", label: "HR Admin" },
  { value: "super_admin", label: "Super Admin" },
];

export function roleOptions(): { value: Role; label: string }[] {
  return ROLE_OPTIONS.map((o) => ({ ...o, label: formatRole(o.value) }));
}

// --- Event types ------------------------------------------------------------

const EVENT_TYPE_FALLBACK: Record<EventType, string> = {
  clock_in: "Check in",
  clock_out: "Check out",
  break_start: "Break Start",
  break_end: "Break End",
};

export function formatEventType(type: EventType): string {
  return tEnum("eventTypes", type, EVENT_TYPE_FALLBACK[type] ?? type);
}

// --- Clock status -----------------------------------------------------------

const CLOCK_STATUS_FALLBACK: Record<ClockStatus, string> = {
  not_clocked_in: "Off Shift",
  clocked_in: "On Shift",
  on_break: "On Break",
};

export function formatClockStatus(status: ClockStatus): string {
  return tEnum("clockStatuses", status, CLOCK_STATUS_FALLBACK[status] ?? status);
}

// --- Employment types -------------------------------------------------------

const EMPLOYMENT_TYPE_FALLBACK: Record<EmploymentType, string> = {
  full_time: "Full Time",
  part_time: "Part Time",
  contractor: "Contractor",
};

export function formatEmploymentType(type: EmploymentType): string {
  return tEnum("employmentTypes", type, EMPLOYMENT_TYPE_FALLBACK[type] ?? type);
}

export const EMPLOYMENT_TYPE_OPTIONS: readonly {
  value: EmploymentType;
  label: string;
}[] = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "contractor", label: "Contractor" },
];

export function employmentTypeOptions(): { value: EmploymentType; label: string }[] {
  return EMPLOYMENT_TYPE_OPTIONS.map((o) => ({
    ...o,
    label: formatEmploymentType(o.value),
  }));
}

// --- Break types ------------------------------------------------------------

const BREAK_TYPE_FALLBACK: Record<BreakType, string> = {
  tea: "Tea",
  lunch: "Lunch",
  other: "Other",
};

export function formatBreakType(type: BreakType): string {
  return tEnum("breakTypes", type, BREAK_TYPE_FALLBACK[type] ?? type);
}

export const BREAK_TYPE_OPTIONS: readonly { value: BreakType; label: string }[] = [
  { value: "tea", label: "Tea" },
  { value: "lunch", label: "Lunch" },
  { value: "other", label: "Other" },
];

// --- Break policies ---------------------------------------------------------

const BREAK_POLICY_FALLBACK: Record<BreakPolicy, string> = {
  none: "No Breaks",
  fixed: "Fixed Break",
  auto_deduct: "Auto-Deduct",
  manual: "Manual Tracking",
};

export function formatBreakPolicy(policy: BreakPolicy): string {
  return tEnum("breakPolicies", policy, BREAK_POLICY_FALLBACK[policy] ?? policy);
}

export const BREAK_POLICY_OPTIONS: readonly {
  value: BreakPolicy;
  label: string;
}[] = [
  { value: "none", label: "No Breaks" },
  { value: "fixed", label: "Fixed Break" },
  { value: "auto_deduct", label: "Auto-Deduct" },
  { value: "manual", label: "Manual Tracking" },
];

// --- Auth methods -----------------------------------------------------------

const AUTH_METHOD_FALLBACK: Record<AuthMethod, string> = {
  passkey: "Passkey",
  pin: "PIN Only",
  both: "Both",
};

export function formatAuthMethod(method: AuthMethod): string {
  return tEnum("authMethods", method, AUTH_METHOD_FALLBACK[method] ?? method);
}

export const AUTH_METHOD_OPTIONS: readonly { value: AuthMethod; label: string }[] = [
  { value: "passkey", label: "Passkey" },
  { value: "pin", label: "PIN Only" },
  { value: "both", label: "Both" },
];

export function authMethodOptions(): { value: AuthMethod; label: string }[] {
  return AUTH_METHOD_OPTIONS.map((o) => ({
    ...o,
    label: formatAuthMethod(o.value),
  }));
}

// --- Attendance (report rows) -----------------------------------------------

const ATTENDANCE_STATUS_FALLBACK: Record<AttendanceStatus, string> = {
  present: "Present",
  absent: "Absent",
  incomplete: "Incomplete",
};

const ATTENDANCE_STATUS_TONE: Record<AttendanceStatus, string> = {
  present: "bg-green-50 text-green-700",
  absent: "bg-red-50 text-red-700",
  incomplete: "bg-amber-50 text-amber-800",
};

export function formatAttendanceStatus(status: AttendanceStatus): string {
  return tEnum(
    "attendanceStatuses",
    status,
    ATTENDANCE_STATUS_FALLBACK[status] ?? status,
  );
}

export function attendanceStatusTone(status: AttendanceStatus): string {
  return ATTENDANCE_STATUS_TONE[status] ?? "bg-gray-100 text-gray-700";
}

// --- Leave statuses ---------------------------------------------------------

const LEAVE_STATUS_FALLBACK: Record<LeaveStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  amended: "Amended",
  amendment_accepted: "Amendment Accepted",
  amendment_rejected: "Amendment Declined",
};

const LEAVE_STATUS_TONE: Record<LeaveStatus, string> = {
  pending: "bg-amber-50 text-amber-800",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
  amended: "bg-orange-50 text-orange-800",
  amendment_accepted: "bg-green-50 text-green-700",
  amendment_rejected: "bg-amber-50 text-amber-800",
};

export function formatLeaveStatus(status: LeaveStatus): string {
  return tEnum(
    "leaveStatuses",
    status,
    LEAVE_STATUS_FALLBACK[status] ?? status,
  );
}

export function leaveStatusTone(status: LeaveStatus): string {
  return LEAVE_STATUS_TONE[status] ?? "bg-gray-100 text-gray-700";
}

// --- Gender -----------------------------------------------------------------

const GENDER_FALLBACK: Record<Gender, string> = {
  male: "Male",
  female: "Female",
  non_binary: "Non-Binary",
  prefer_not_to_say: "Prefer Not to Say",
  other: "Other",
};

export function formatGender(gender: Gender): string {
  return tEnum("genders", gender, GENDER_FALLBACK[gender] ?? gender);
}

export const GENDER_OPTIONS: readonly { value: Gender; label: string }[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-Binary" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
  { value: "other", label: "Other" },
];

export function genderOptions(): { value: Gender; label: string }[] {
  return GENDER_OPTIONS.map((o) => ({ ...o, label: formatGender(o.value) }));
}

// --- Marital status ---------------------------------------------------------

const MARITAL_STATUS_FALLBACK: Record<MaritalStatus, string> = {
  single: "Single",
  married: "Married",
  civil_partnership: "Civil Partnership",
  divorced: "Divorced",
  widowed: "Widowed",
  prefer_not_to_say: "Prefer Not to Say",
};

export function formatMaritalStatus(status: MaritalStatus): string {
  return tEnum("maritalStatuses", status, MARITAL_STATUS_FALLBACK[status] ?? status);
}

export const MARITAL_STATUS_OPTIONS: readonly { value: MaritalStatus; label: string }[] = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "civil_partnership", label: "Civil Partnership" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
];

export function maritalStatusOptions(): { value: MaritalStatus; label: string }[] {
  return MARITAL_STATUS_OPTIONS.map((o) => ({ ...o, label: formatMaritalStatus(o.value) }));
}

// --- Pay type ---------------------------------------------------------------

const PAY_TYPE_FALLBACK: Record<PayType, string> = {
  salary: "Salary",
  hourly: "Hourly",
  daily: "Daily",
};

export function formatPayType(payType: PayType): string {
  return tEnum("payTypes", payType, PAY_TYPE_FALLBACK[payType] ?? payType);
}

export const PAY_TYPE_OPTIONS: readonly { value: PayType; label: string }[] = [
  { value: "salary", label: "Salary" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
];

export function payTypeOptions(): { value: PayType; label: string }[] {
  return PAY_TYPE_OPTIONS.map((o) => ({ ...o, label: formatPayType(o.value) }));
}

// --- Pay schedule -----------------------------------------------------------

const PAY_SCHEDULE_FALLBACK: Record<PaySchedule, string> = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  custom: "Custom",
};

export function formatPaySchedule(schedule: PaySchedule): string {
  return tEnum("paySchedules", schedule, PAY_SCHEDULE_FALLBACK[schedule] ?? schedule);
}

export const PAY_SCHEDULE_OPTIONS: readonly { value: PaySchedule; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

export function payScheduleOptions(): { value: PaySchedule; label: string }[] {
  return PAY_SCHEDULE_OPTIONS.map((o) => ({ ...o, label: formatPaySchedule(o.value) }));
}

// --- Pay frequency ----------------------------------------------------------

const PAY_FREQUENCY_FALLBACK: Record<PayFrequency, string> = {
  hour: "Per Hour",
  day: "Per Day",
  week: "Per Week",
  month: "Per Month",
  year: "Per Year",
};

export function formatPayFrequency(freq: PayFrequency): string {
  return tEnum("payFrequencies", freq, PAY_FREQUENCY_FALLBACK[freq] ?? freq);
}

export const PAY_FREQUENCY_OPTIONS: readonly { value: PayFrequency; label: string }[] = [
  { value: "hour", label: "Per Hour" },
  { value: "day", label: "Per Day" },
  { value: "week", label: "Per Week" },
  { value: "month", label: "Per Month" },
  { value: "year", label: "Per Year" },
];

export function payFrequencyOptions(): { value: PayFrequency; label: string }[] {
  return PAY_FREQUENCY_OPTIONS.map((o) => ({ ...o, label: formatPayFrequency(o.value) }));
}

// --- Visa status ------------------------------------------------------------

const VISA_STATUS_FALLBACK: Record<VisaStatus, string> = {
  active: "Active",
  expired: "Expired",
  pending: "Pending",
  revoked: "Revoked",
};

const VISA_STATUS_TONE: Record<VisaStatus, string> = {
  active: "bg-green-50 text-green-700",
  expired: "bg-red-50 text-red-700",
  pending: "bg-amber-50 text-amber-800",
  revoked: "bg-gray-100 text-gray-500",
};

export function formatVisaStatus(status: VisaStatus): string {
  return tEnum("visaStatuses", status, VISA_STATUS_FALLBACK[status] ?? status);
}

export function visaStatusTone(status: VisaStatus): string {
  return VISA_STATUS_TONE[status] ?? "bg-gray-100 text-gray-700";
}

// --- Requisition status -----------------------------------------------------

const REQUISITION_STATUS_FALLBACK: Record<RequisitionStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  ordered: "Ordered",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const REQUISITION_STATUS_TONE: Record<RequisitionStatus, string> = {
  pending: "bg-amber-50 text-amber-800",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  ordered: "bg-blue-50 text-blue-700",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export function formatRequisitionStatus(status: RequisitionStatus): string {
  return tEnum(
    "requisitionStatuses",
    status,
    REQUISITION_STATUS_FALLBACK[status] ?? status,
  );
}

export function requisitionStatusTone(status: RequisitionStatus): string {
  return REQUISITION_STATUS_TONE[status] ?? "bg-gray-100 text-gray-700";
}

// --- Employment status ------------------------------------------------------

export type EmploymentStatus = "active" | "probation" | "suspended" | "terminated" | "notice_period";

const EMPLOYMENT_STATUS_FALLBACK: Record<string, string> = {
  active: "Active",
  probation: "Probation",
  suspended: "Suspended",
  terminated: "Terminated",
  notice_period: "Notice Period",
};

export function formatEmploymentStatus(status: string): string {
  return tEnum("employmentStatuses", status, EMPLOYMENT_STATUS_FALLBACK[status] ?? status);
}

// --- Generic field value formatter for profile fields -----------------------

const FIELD_FORMATTERS: Record<string, (val: string) => string> = {
  gender: (v) => formatGender(v as Gender),
  marital_status: (v) => formatMaritalStatus(v as MaritalStatus),
  employment_type: (v) => formatEmploymentType(v as EmploymentType),
  role: (v) => formatRole(v as Role),
  auth_method: (v) => formatAuthMethod(v as AuthMethod),
  pay_type: (v) => formatPayType(v as PayType),
  pay_schedule: (v) => formatPaySchedule(v as PaySchedule),
  pay_frequency: (v) => formatPayFrequency(v as PayFrequency),
};

export function formatFieldValue(fieldName: string, rawValue: string | null): string {
  if (!rawValue) return "";
  const formatter = FIELD_FORMATTERS[fieldName];
  return formatter ? formatter(rawValue) : rawValue;
}

// --- Leave type name (DB name → translated via code) ------------------------

export function formatLeaveTypeName(
  code: string | null | undefined,
  dbName: string | null | undefined,
): string {
  if (!code) return dbName ?? "";
  return tEnum("leaveTypes", code, dbName ?? code);
}

// --- Location ---------------------------------------------------------------

export function formatCoordinates(
  lat: number | null,
  lng: number | null,
): string | null {
  if (lat === null || lng === null) return null;
  const ns = lat >= 0 ? "N" : "S";
  const ew = lng >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(4)}° ${ns}, ${Math.abs(lng).toFixed(4)}° ${ew}`;
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return "—";
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m.toString().padStart(2, "0")}m` : `${m}m`;
}
